import { useState } from 'react';
import { FileText } from 'lucide-react';
import { Button } from '../../components/atoms/Button';
import { Textarea } from '../../components/atoms/Textarea';
import { DraftPhaseLoader } from '../../components/loaders/DraftPhaseLoader';
import type { DraftPhase, QuestionAnswer } from './types';

export { DraftTemplateOverlay } from './DraftTemplateOverlay';

/**
 * Draft-mode overlays for QnA + prompt hint. The loading overlay for
 * template / questions / draft / instant phases is now DraftPhaseLoader —
 * a Framer-Motion port of frontend-prod's DraftCreationLoader, scaled for
 * the 360px task pane.
 *
 * All mount via `absolute inset-0 z-20` inside TaskPane.
 */

export function DraftLoadingOverlay({
  phase,
  onStop,
}: {
  phase: DraftPhase;
  onStop?: () => void;
}): JSX.Element {
  return <DraftPhaseLoader phase={phase} onStop={onStop} />;
}

/**
 * QnA phase. Non-dismissible by design — the only outs are:
 *   - "Generate draft" (submit answers, even blank)
 *   - "Stop" (cancel just sends chat/stop; overlay stays up)
 *
 * Parent controls abandonment via an explicit reset action outside this overlay
 * (e.g. the New-chat button in the header).
 */
export function DraftQnaOverlay({
  questions,
  onAnswer,
  onSubmit,
  onStop,
  busy,
}: {
  questions: QuestionAnswer[];
  onAnswer: (id: string, answer: string) => void;
  onSubmit: () => void;
  onStop?: () => void;
  busy?: boolean;
}): JSX.Element {
  const answered = questions.filter((q) => (q.answer ?? '').trim().length > 0).length;
  const pct = questions.length === 0 ? 0 : Math.round((answered / questions.length) * 100);

  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-background">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="w-4 h-4 text-primary flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold">Answer questions</div>
            <div className="text-[11px] text-muted-foreground">
              {answered}/{questions.length} answered ({pct}%). Blank answers are allowed.
            </div>
          </div>
        </div>
        {onStop && (
          <Button variant="ghost" size="sm" onClick={onStop} disabled={busy}>
            Stop
          </Button>
        )}
      </div>

      <div className="h-1 bg-border">
        <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3 scrollbar-thin space-y-3">
        {questions.length === 0 ? (
          <div className="text-xs text-muted-foreground italic py-10 text-center">
            No questions returned. Tap Stop to cancel and try a different template.
          </div>
        ) : (
          questions.map((q, i) => (
            <div key={q.id} className="space-y-1">
              <label className="text-xs font-medium text-foreground block">
                {i + 1}. {q.question}
              </label>
              <Textarea
                value={q.answer ?? ''}
                onChange={(e) => onAnswer(q.id, e.target.value)}
                placeholder="Your answer (optional)…"
                maxHeight={120}
                className="text-sm"
              />
            </div>
          ))
        )}
      </div>

      <div className="border-t border-border px-3 py-2">
        <Button
          variant="default"
          size="sm"
          className="w-full"
          onClick={onSubmit}
          disabled={busy || questions.length === 0}
        >
          Generate draft
        </Button>
      </div>
    </div>
  );
}

export function DraftPromptHint(): JSX.Element {
  const [tip] = useState(
    'Describe the document you want. Example: "Draft a tenancy agreement for a 2BHK in Mumbai, 11-month lease, rent ₹30,000, 2-month deposit."',
  );
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6">
      <div className="w-10 h-10 rounded-full flex items-center justify-center mb-3 bg-muted text-foreground/70">
        <FileText className="w-5 h-5" />
      </div>
      <h3 className="text-sm font-semibold mb-1">Draft mode</h3>
      <p className="text-xs text-muted-foreground max-w-xs">{tip}</p>
    </div>
  );
}
